import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Info as InfoIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  SwapHoriz as SwapHorizIcon,
  CalendarToday as CalendarIcon,
  Pets as PetsIcon,
  Apartment as ApartmentIcon,
  Map as MapIcon,
  LocationCity as LocationCityIcon,
  Notes as NotesIcon,
  // CheckBox as CheckBoxIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Email
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useState } from 'react';
import axios from 'axios';
import Container from '../../../components/layout/Container';
import Fieldset from '../../../components/Form/Fieldset';
import Row from '../../../components/Form/Row';
import Form from '../../../components/Form/Form';
import AppBarComponent from '../../../components/AppBarComponent';
import ButtonArea from '../../../components/Form/ButtonArea';
import EuroInput from '../../../components/inputs/EuroInput';

// Estilos idênticos ao cadastro de imóvel
const ButtonAreaStyled = styled(ButtonArea)`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const StyledFieldset = styled(Fieldset)`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.12);
  }
`;

const SectionTitle = styled(Typography)`
  color: #424242;
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
`;

const FormTitle = styled(Typography)`
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 32px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 12px;
`;

// const statusOptions = [
//   { value: 'CREATED', label: 'Created' },
//   { value: 'IN_PROGRESS', label: 'Em Andamento' },
//   { value: 'COMPLETED', label: 'Concluída' },
//   { value: 'CANCELLED', label: 'Cancelada' }
// ];

const propertyTypes = [
  { value: 'APARTMENTO', label: 'Apartamento' },
  { value: 'CASA', label: 'Casa' },
  { value: 'COMERCIAL', label: 'Comercial' },
  { value: 'TERRENO', label: 'Terreno' }
];

const categories = [
  { value: 'T1', label: 'T1' },
  { value: 'T2', label: 'T2' },
  { value: 'T3', label: 'T3' },
  { value: 'T4+', label: 'T4+' }
];

const buildApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    console.error('API_BASE_URL não está definido no arquivo .env');
    return endpoint;
  }
  const normalizedBase = baseUrl.replace(/\/+$/, '') + '/';
  return new URL(endpoint, normalizedBase).toString();
};

export default function CadastroRealocationPage() {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
    watch,
    setValue
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      titleRelocation: '',
      status: 'PENDING',
      cost: '',
      clientId: '',
      clientName: '',
      originAddress: '',
      originCity: '',
      originNeighborhood: '',
      originState: '',
      destinationAddress: '',
      destinationCity: '',
      destinationNeighborhood: '',
      destinationState: '',
      propertyType: '',
      category: '',
      petFriendly: false,
      observation: '',
      startDate: '',
      expectedEndDate: ''
    }
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [clients, setClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isSearchingProperties, setIsSearchingProperties] = useState(false);

  const onSubmit = async (data) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.token) {
        throw new Error('Usuário não autenticado');
      }

      const payload = {
        titleRelocation: data.name.trim(),
        clientName: data.clientName,
        email: data.email,
        status: data.status,
        cost: parseFloat(data.cost.replace(/[^\d,]/g, '').replace(',', '.')),
        clientId: data.clientId,
        originAddress: {
          street: data.originAddress.trim(),
          city: data.originCity.trim(),
          neighborhood: data.originNeighborhood.trim(),
          state: data.originState.trim().toUpperCase(),
          country: 'Brasil'
        },
        destinationAddress: {
          street: data.destinationAddress.trim(),
          city: data.destinationCity.trim(),
          neighborhood: data.destinationNeighborhood.trim(),
          state: data.destinationState.trim().toUpperCase(),
          country: 'Brasil'
        },
        propertyType: data.propertyType,
        category: data.category,
        petFriendly: data.petFriendly,
        observation: data.observation?.trim() || null,
        startDate: data.startDate,
        expectedEndDate: data.expectedEndDate
      };

      const response = await axios.post(
        buildApiUrl('realocation'),
        payload,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status !== 204) {
        throw new Error('Erro ao cadastrar realocação');
      }

      setSnackbarMessage('Realocação cadastrada com sucesso!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      reset();
      setSelectedClient(null);
      setClients([]);
      setClientSearchTerm('');
      setSelectedProperty(null);
      setProperties([]);
      setPropertySearchTerm('');
    } catch (error) {
      console.error('Erro ao cadastrar realocação:', error);
      let errorMessage = 'Erro ao cadastrar realocação';

      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleClientSearch = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.token) {
        throw new Error('Usuário não autenticado');
      }

      const response = await axios.get(
        buildApiUrl(`customers/search?name=${clientSearchTerm}`),
        {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        }
      );

      setClients(response.data);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setSnackbarMessage('Erro ao buscar clientes');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setValue('clientId', client.id);
    setValue('clientName', client.name);
    setValue('email', client.email);


    // Preenche os campos de endereço de origem
    if (client.address) {
      setValue('originAddress', client.address.street || '');
      setValue('originNeighborhood', client.address.neighborhood || '');
      setValue('originCity', client.address.city || '');
      setValue('originState', client.address.state || '');
    }

    setClients([]);
    setClientSearchTerm(client.name);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setValue('clientId', '');
    setValue('clientName', '');
    setValue('originAddress', '');
    setValue('originNeighborhood', '');
    setValue('originCity', '');
    setValue('originState', '');
    setClientSearchTerm('');
    setClients([]);
  };

  const handlePropertySearch = async () => {
    try {
      setIsSearchingProperties(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.token) {
        throw new Error('Usuário não autenticado');
      }

      const response = await axios.get(
        buildApiUrl(`real-estate/search?title=${encodeURIComponent(propertySearchTerm)}`),
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Accept': 'application/json'
          }
        }
      );

      setProperties(response.data);
    } catch (error) {
      console.error('Erro ao buscar imóveis:', error);
      setSnackbarMessage('Erro ao buscar imóveis');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSearchingProperties(false);
    }
  };

  const handlePropertySelect = (property) => {
    console.log('Property selected:', property); // Para debug (pode remover depois)

    setSelectedProperty(property);

    // Preenche os campos de endereço de destino
    if (property.address) {
      setValue('destinationAddress', property.address.street || '');
      setValue('destinationNeighborhood', property.address.neighborhood || '');
      setValue('destinationCity', property.address.city || '');
      // Garante que o estado tenha exatamente 2 caracteres em maiúsculas
      setValue('destinationState', property.address.state?.substring(0, 2).toUpperCase() || '');
    } else {
      // Limpa os campos se não houver endereço
      setValue('destinationAddress', '');
      setValue('destinationNeighborhood', '');
      setValue('destinationCity', '');
      setValue('destinationState', '');
    }

    // Preenche tipo e categoria (convertendo para maiúsculas)
    setValue('propertyType', property.type?.toUpperCase() || '');
    setValue('category', property.typology?.toUpperCase() || '');

    // Atualiza petFriendly apenas se existir no objeto
    if (typeof property.acceptsPets !== 'undefined') {
      setValue('petFriendly', Boolean(property.acceptsPets));
    }

    // Limpa a lista e atualiza o campo de busca
    setProperties([]);
    setPropertySearchTerm(property.title || '');
  };

  const handleClearProperty = () => {
    setSelectedProperty(null);
    setValue('destinationAddress', '');
    setValue('destinationNeighborhood', '');
    setValue('destinationCity', '');
    setValue('destinationState', '');
    setPropertySearchTerm('');
    setProperties([]);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const observationLength = watch('observation')?.length || 0;

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBarComponent title="Cadastro de Realocação" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <FormTitle variant="h1">
            <SwapHorizIcon fontSize="large" />
            Nova Realocação
          </FormTitle>

          <Typography variant="body1" color="text.secondary" mb={4}>
            Preencha os campos abaixo para cadastrar uma nova realocação.
          </Typography>

          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* Seção Informações Básicas */}
            <StyledFieldset>
              <SectionTitle variant="h2">
                <InfoIcon color="primary" />
                Informações Básicas
              </SectionTitle>

              <Row columns={2}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: 'Nome é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Nome deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome da Realocação *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Mudança Família Silva"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'E-mail é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'E-mail inválido'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="E-mail *"
                      fullWidth
                      type="email"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="exemplo@email.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

              </Row>

              <Row columns={3}>
                <EuroInput
                  control={control}
                  name="cost"
                  label="Custo em Euros *"
                  rules={{
                    required: 'Por favor, insira o valor estimado',
                    min: {
                      value: '€ 100,00',
                      message: 'O valor mínimo é € 100,00'
                    }
                  }}
                />

                <Controller
                  name="propertyType"
                  control={control}
                  rules={{ required: 'Tipo de imóvel é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Imóvel *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {propertyTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />

                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Categoria é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Categoria *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CategoryIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>

              <Row columns={2}>
                <Controller
                  name="startDate"
                  control={control}
                  rules={{ required: 'Data de início é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Data de Início *"
                      type="date"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                        inputProps: {
                          max: watch('expectedEndDate') || '2100-12-31'
                        }
                      }}
                    />
                  )}
                />

                <Controller
                  name="expectedEndDate"
                  control={control}
                  rules={{
                    required: 'Data prevista de término é obrigatória',
                    validate: value =>
                      !watch('startDate') || value >= watch('startDate') ||
                      'Data deve ser posterior à data de início'
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Data Prevista de Término *"
                      type="date"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                        inputProps: {
                          min: watch('startDate') || '1900-01-01'
                        }
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={1}>
                <Controller
                  name="petFriendly"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                          icon={<PetsIcon />}
                          checkedIcon={<PetsIcon color="primary" />}
                        />
                      }
                      label="Aceita animais de estimação"
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            {/* Seção Cliente */}
            <StyledFieldset>
              <SectionTitle variant="h2">
                <PersonIcon color="primary" />
                Cliente
                <Tooltip title="Busque o cliente para preencher automaticamente o endereço de origem">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>

              <Row columns={2}>
                <TextField
                  label="Buscar Cliente"
                  fullWidth
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Digite o nome do cliente"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: selectedClient && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClearClient} size="small">
                          <PersonIcon color="error" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleClientSearch}
                  disabled={!clientSearchTerm.trim()}
                  sx={{ height: '56px' }}
                >
                  Buscar
                </Button>
              </Row>

              {selectedClient && (
                <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                  <Typography variant="subtitle1">
                    Cliente selecionado: <strong>{selectedClient.name}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedClient.email} | {selectedClient.phone}
                  </Typography>
                </Box>
              )}

              {clients.length > 0 && !selectedClient && (
                <Box mt={2}>
                  <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {clients.map((client) => (
                      <div key={client.id}>
                        <ListItem
                          button
                          onClick={() => handleClientSelect(client)}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#e3f2fd'
                            }
                          }}
                        >
                          <ListItemText
                            primary={client.name}
                            secondary={`${client.email} | ${client.phone}`}
                          />
                        </ListItem>
                        <Divider />
                      </div>
                    ))}
                  </List>
                </Box>
              )}
            </StyledFieldset>

            {/* Seção Endereço de Origem */}
            <StyledFieldset>
              <SectionTitle variant="h2">
                <HomeIcon color="primary" />
                Endereço de Origem
              </SectionTitle>

              <Row columns={2}>
                <Controller
                  name="originAddress"
                  control={control}
                  rules={{
                    required: 'Endereço de origem é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Endereço deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Endereço de Origem *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Av. Paulista, 1000"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="originNeighborhood"
                  control={control}
                  rules={{
                    required: 'Bairro de origem é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Bairro deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Bairro de Origem *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Bela Vista"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={3}>
                <Controller
                  name="originCity"
                  control={control}
                  rules={{
                    required: 'Cidade de origem é obrigatória',
                    minLength: {
                      value: 3,
                      message: 'Cidade deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cidade de Origem *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: São Paulo"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="originState"
                  control={control}
                  rules={{
                    required: 'Estado de origem é obrigatório',
                    minLength: {
                      value: 2,
                      message: 'Estado deve ter 2 caracteres'
                    },
                    maxLength: {
                      value: 2,
                      message: 'Estado deve ter 2 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Estado (UF) de Origem *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Ex: SP'}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        maxLength: 2,
                        style: { textTransform: 'uppercase' }
                      }}
                      placeholder="SP"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            {/* Seção Busca de Imóvel de Destino */}
            <StyledFieldset>
              <SectionTitle variant="h2">
                <SearchIcon color="primary" />
                Buscar Imóvel de Destino
                <Tooltip title="Busque um imóvel para preencher automaticamente os campos de destino">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>

              <Row columns={2}>
                <TextField
                  label="Buscar Imóvel"
                  fullWidth
                  value={propertySearchTerm}
                  onChange={(e) => setPropertySearchTerm(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Digite o título ou endereço do imóvel"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: selectedProperty && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClearProperty} size="small">
                          <HomeIcon color="error" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handlePropertySearch}
                  disabled={!propertySearchTerm.trim() || isSearchingProperties}
                  sx={{ height: '56px' }}
                >
                  {isSearchingProperties ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Buscar Imóvel'
                  )}
                </Button>
              </Row>

              {selectedProperty && (
                <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                  <Typography variant="subtitle1">
                    Imóvel selecionado: <strong>{selectedProperty.title}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedProperty.type} | {selectedProperty.typology} | {selectedProperty.acceptsPets ? 'Aceita pets' : 'Não aceita pets'}
                  </Typography>
                </Box>
              )}

              {properties.length > 0 && !selectedProperty && (
                <Box mt={2}>
                  <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {properties.map((property) => (
                      <div key={property.id}>
                        <ListItem
                          button
                          onClick={() => handlePropertySelect(property)}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#e3f2fd'
                            }
                          }}
                        >
                          <ListItemText
                            primary={property.title}
                            secondary={`${property.type} | ${property.typology} | ${property.acceptsPets ? 'Aceita pets' : 'Não aceita pets'}`}
                          />
                        </ListItem>
                        <Divider />
                      </div>
                    ))}
                  </List>
                </Box>
              )}
            </StyledFieldset>

            {/* Seção Endereço de Destino */}
            <StyledFieldset>
              <SectionTitle variant="h2">
                <HomeIcon color="primary" />
                Endereço de Destino
              </SectionTitle>

              <Row columns={2}>
                <Controller
                  name="destinationAddress"
                  control={control}
                  rules={{
                    required: 'Endereço de destino é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Endereço deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Endereço de Destino *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Rua Oscar Freire, 500"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="destinationNeighborhood"
                  control={control}
                  rules={{
                    required: 'Bairro de destino é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Bairro deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Bairro de Destino *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Jardins"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={3}>
                <Controller
                  name="destinationCity"
                  control={control}
                  rules={{
                    required: 'Cidade de destino é obrigatória',
                    minLength: {
                      value: 3,
                      message: 'Cidade deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cidade de Destino *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: São Paulo"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="destinationState"
                  control={control}
                  rules={{
                    required: 'Estado de destino é obrigatório',
                    minLength: {
                      value: 2,
                      message: 'Estado deve ter 2 caracteres'
                    },
                    maxLength: {
                      value: 2,
                      message: 'Estado deve ter 2 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Estado (UF) de Destino *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Ex: SP'}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        maxLength: 2,
                        style: { textTransform: 'uppercase' }
                      }}
                      placeholder="SP"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            {/* Seção Observações */}
            <StyledFieldset>
              <SectionTitle variant="h2">
                <NotesIcon color="primary" />
                Observações
                <Tooltip title="Informações adicionais sobre a realocação">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>

              <Row columns={1}>
                <Controller
                  name="observation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Observações Adicionais"
                      multiline
                      rows={4}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        maxLength: 500
                      }}
                      placeholder="Detalhes importantes sobre a realocação (opcional)"
                      helperText={`${observationLength}/500 caracteres`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <NotesIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>


            {/* Botões de Ação */}
            <ButtonAreaStyled>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  reset();
                  setSelectedClient(null);
                  setClients([]);
                  setClientSearchTerm('');
                  setSelectedProperty(null);
                  setProperties([]);
                  setPropertySearchTerm('');
                }}
                disabled={isSubmitting}
                sx={{
                  minWidth: 120,
                  px: 3,
                  py: 1,
                  borderRadius: 2
                }}
              >
                Limpar
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || !isValid}
                sx={{
                  minWidth: 200,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Cadastrar Realocação'
                )}
              </Button>
            </ButtonAreaStyled>
          </Form>
        </Paper>

        {/* Snackbar para feedback */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}